import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Box,
  Stack,
  Divider,
  InputAdornment,
  IconButton,
  Button,
} from "@mui/material";
import {
  Close as CloseIcon,
  Code as CodeIcon,
  Subject as SubjectIcon,
  TextFields as TextFieldsIcon,
  Info as InfoIcon,
  Save as SaveIcon,
} from "@mui/icons-material";

const emptyForm = {
  template_code: "",
  version: "",
  subject: "",
  body: "",
  is_active: true,
};

function PatchingTemplateFormDialog({
  open,
  mode,
  template,
  onClose,
  onSubmit,
  submitting,
}) {
  const [values, setValues] = useState(emptyForm);
  const [touched, setTouched] = useState(false);

  const bodyCharCount = values.body ? values.body.length : 0;
  const subjectCharCount = values.subject ? values.subject.length : 0;

  const isEdit = mode === "edit";

  useEffect(() => {
    if (!open) return;
    setTouched(false);
    if (isEdit && template) {
      setValues({
        template_code: template.template_code ?? "",
        version: template.version != null ? String(template.version) : "",
        subject: template.subject ?? "",
        body: template.body ?? "",
        is_active: Boolean(template.is_active),
      });
    } else {
      setValues(emptyForm);
    }
  }, [open, isEdit, template]);

  const handleChange = (field) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setValues((prev) => ({ ...prev, [field]: v }));
  };

  const errors = () => {
    const e = {};
    if (!String(values.template_code).trim()) {
      e.template_code = "Required";
    }
    if (!String(values.subject).trim()) {
      e.subject = "Required";
    }
    if (!String(values.body).trim()) {
      e.body = "Required";
    }
    if (!isEdit && values.version !== "" && values.version != null) {
      const n = parseInt(values.version, 10);
      if (Number.isNaN(n) || n < 1) {
        e.version = "Must be a positive integer";
      }
    }
    return e;
  };

  const err = touched ? errors() : {};

  const autoIncrementVersion = (version) => {
    return parseInt(version ?? 1, 10) + 1;
  };

  const handleSubmit = () => {
    setTouched(true);
    const e = errors();
    if (Object.keys(e).length) return;

    if (isEdit) {
      onSubmit({
        t_id: template.t_id,
        payload: {
          subject: String(values.subject).trim(),
          body: String(values.body),
          is_active: values.is_active,
          version: autoIncrementVersion(values.version),
        },
      });
      return;
    }

    const payload = {
      template_code: String(values.template_code).trim(),
      subject: String(values.subject).trim(),
      body: String(values.body),
      is_active: values.is_active,
    };
    if (values.version !== "" && values.version != null) {
      payload.version = parseInt(values.version, 10);
    }
    onSubmit({ payload });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth='md'
      fullWidth
      fullScreen={window.innerWidth < 600} // 移动端全屏体验
      PaperProps={{
        sx: {
          borderRadius: 4,
          backgroundImage: "none",
          boxShadow: "0 20px 40px rgba(0,0,0,0.15)",
          overflow: "visible",
        },
      }}
    >
      {/* 1. 头部：标题与关闭 */}
      <Box
        sx={{
          px: 3,
          py: 2.5,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Stack direction='row' spacing={1.5} alignItems='center'>
          <Box
            sx={{
              p: 1,
              bgcolor: "primary.lighter",
              borderRadius: 2,
              display: "flex",
            }}
          >
            <CodeIcon sx={{ color: "primary.main", fontSize: 24 }} />
          </Box>
          <Box>
            <Typography variant='h6' fontWeight={700} color='text.primary'>
              {isEdit ? "Edit Template" : "Create Template"}
            </Typography>
            <Typography variant='caption' color='text.secondary'>
              {isEdit
                ? `Modifying version ${values.version}`
                : "Define a new notification structure"}
            </Typography>
          </Box>
        </Stack>
        <IconButton
          onClick={onClose}
          size='small'
          sx={{ color: "text.secondary" }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent sx={{ p: 3 }}>
        <Stack spacing={3.5}>
          {/* 2. 区块一：基础标识 (Identity) */}
          <Box>
            <Typography
              variant='subtitle2'
              gutterBottom
              sx={{ display: "flex", alignItems: "center", gap: 1 }}
            >
              <InfoIcon fontSize='small' color='action' />
              Identity & Versioning
            </Typography>
            <br />
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                label='Template Code'
                value={values.template_code}
                onChange={handleChange("template_code")}
                disabled={isEdit}
                required
                fullWidth
                error={Boolean(err.template_code)}
                helperText={
                  err.template_code || "Unique identifier (e.g., PROMO_01)"
                }
                InputProps={{
                  startAdornment: (
                    <InputAdornment position='start'>
                      <CodeIcon fontSize='small' color='action' />
                    </InputAdornment>
                  ),
                  sx: {
                    fontFamily: "monospace",
                    bgcolor: isEdit ? "action.hover" : "transparent",
                  },
                }}
                variant='outlined'
                size='small'
              />
              <TextField
                label='Version'
                type='number'
                value={values.version}
                onChange={handleChange("version")}
                disabled={isEdit}
                fullWidth
                inputProps={{ min: 1, step: 0.1 }}
                error={Boolean(err.version)}
                helperText={err.version || " "}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position='start'>
                      <TextFieldsIcon fontSize='small' color='action' />
                    </InputAdornment>
                  ),
                }}
                size='small'
                variant='outlined'
              />
            </Stack>
          </Box>

          <Divider sx={{ opacity: 0.5 }} />

          {/* 3. 区块二：内容配置 (Content) */}
          <Box>
            <Typography
              variant='subtitle2'
              gutterBottom
              sx={{ display: "flex", alignItems: "center", gap: 1 }}
            >
              <SubjectIcon fontSize='small' color='action' />
              Notification Content
            </Typography>
            <br />
            <Stack spacing={2}>
              <TextField
                label='Subject Line'
                value={values.subject}
                onChange={handleChange("subject")}
                required
                fullWidth
                error={Boolean(err.subject)}
                helperText={err.subject || `${subjectCharCount} chars`}
                placeholder='e.g., System Maintenance Scheduled'
                InputProps={{
                  startAdornment: (
                    <InputAdornment position='start'>
                      <SubjectIcon fontSize='small' color='action' />
                    </InputAdornment>
                  ),
                }}
                variant='outlined'
                size='small'
                sx={{ "& .MuiInputBase-input": { fontWeight: 500 } }}
              />

              <TextField
                label='Message Body'
                value={values.body}
                onChange={handleChange("body")}
                required
                fullWidth
                multiline
                minRows={10}
                maxRows={15}
                error={Boolean(err.body)}
                helperText={err.body || `${bodyCharCount} characters`}
                placeholder='Enter the notification message here...'
                sx={{
                  "& .MuiInputBase-root": {
                    fontFamily: "inherit",
                    bgcolor: "background.default",
                    borderRadius: 2,
                    overflow: "hidden",
                  },
                }}
                variant='outlined'
              />
            </Stack>
          </Box>
        </Stack>
      </DialogContent>

      {/* 4. 底部操作栏：状态与保存 */}
      <DialogActions
        sx={{
          px: 3,
          py: 2.5,
          borderTop: "1px solid",
          borderColor: "divider",
          bgcolor: "background.default",
        }}
      >
        <Box sx={{ display: "flex", gap: 1.5 }}>
          <Button
            onClick={onClose}
            disabled={submitting}
            variant='text'
            color='inherit'
          >
            Cancel
          </Button>
          <Button
            variant='contained'
            onClick={handleSubmit}
            disabled={submitting}
            startIcon={submitting ? null : <SaveIcon />}
            sx={{
              borderRadius: 2,
              px: 3,
              boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            }}
          >
            {submitting
              ? "Saving..."
              : isEdit
                ? "Save Changes"
                : "Create Template"}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}

export default PatchingTemplateFormDialog;
